// src/contexts/AuthContext.js

import React, { createContext, useContext, useEffect, useState } from 'react'
import { auth, db } from '../firebase'
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from 'firebase/auth'
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  onSnapshot,
  updateDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore'
import {
  DEFAULT_PLAN_NAME,
  DEFAULT_RESPONSE_LIMIT,
  ensureOrganizationPlanDefaults,
  getNextResetDate,
  normalizePlanData
} from '../utils/planUtils'

const AuthContext = createContext()

export function useAuth() {
  return useContext(AuthContext)
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [loading, setLoading] = useState(true)
  const [authed, setAuthed] = useState(false)
  const [userStatus, setUserStatus] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [isAnonymousUser, setIsAnonymousUser] = useState(false)

  const loadOrganization = async (organizationId) => {
    if (!organizationId) {
      setOrganization(null)
      return
    }
    try {
      const ensuredOrg = await ensureOrganizationPlanDefaults(organizationId)
      if (ensuredOrg) {
        const normalized = normalizePlanData(ensuredOrg)
        setOrganization({ ...ensuredOrg, ...normalized })
      } else {
        setOrganization(null)
      }
    } catch (error) {
      console.error('Organization fetch error:', error)
      setOrganization(null)
    }
  }

  const ensureOrganizationForUser = async (user, userData = {}, userDocRef) => {
    try {
      const emailLower = (user?.email || '').toLowerCase()

      if (userData.organizationId) {
        const existingOrg = await getDoc(doc(db, 'organizations', userData.organizationId))
        if (existingOrg.exists()) {
          return { id: existingOrg.id, ...existingOrg.data() }
        }
      }

      if (emailLower) {
        const inviteSnap = await getDocs(
          query(
            collection(db, 'invites'),
            where('emailLower', '==', emailLower),
            where('status', '==', 'pending')
          )
        )

        if (!inviteSnap.empty) {
          const inviteDoc = inviteSnap.docs[0]
          const inviteData = inviteDoc.data()
          const orgId = inviteData.organizationId

          if (orgId) {
            try {
              await updateDoc(inviteDoc.ref, {
                status: 'accepted',
                acceptedAt: serverTimestamp(),
                acceptedUserId: user.uid
              })
            } catch (err) {
              console.error('Invite update error:', err)
            }

            if (userDocRef) {
              await updateDoc(userDocRef, { organizationId: orgId })
            }

            const existingOrg = await getDoc(doc(db, 'organizations', orgId))
            if (existingOrg.exists()) {
              return { id: existingOrg.id, ...existingOrg.data() }
            }
            return { id: orgId }
          }
        }
      }

      const orgName =
        [userData.firstName, userData.lastName].filter(Boolean).join(' ').trim() ||
        userData.organizationName ||
        user?.displayName ||
        user?.email?.split('@')[0] ||
        'Organization'

      const newOrgRef = await addDoc(collection(db, 'organizations'), {
        ownerId: user.uid,
        name: orgName,
        createdAt: serverTimestamp(),
        planName: DEFAULT_PLAN_NAME,
        responseLimit: DEFAULT_RESPONSE_LIMIT,
        responsesUsed: 0,
        responseResetAt: Timestamp.fromDate(getNextResetDate())
      })

      if (userDocRef) {
        await updateDoc(userDocRef, { organizationId: newOrgRef.id })
      }

      return {
        id: newOrgRef.id,
        ownerId: user.uid,
        name: orgName,
        planName: DEFAULT_PLAN_NAME,
        responseLimit: DEFAULT_RESPONSE_LIMIT,
        responsesUsed: 0,
        responseResetAt: Timestamp.fromDate(getNextResetDate())
      }
    } catch (error) {
      console.error('Organization creation error:', error)
      return null
    }
  }

  // 1) Email/Password ile yeni kullanıcı kaydı (Register)
  async function register(email, password) {
    // Firebase Authentication’da kullanıcı oluştur
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const { uid } = userCredential.user
    const emailLower = (email || '').toLowerCase()

    const inviteSnap = await getDocs(
      query(
        collection(db, 'invites'),
        where('emailLower', '==', emailLower),
        where('status', '==', 'pending')
      )
    )

    let organizationIdToUse = null
    if (!inviteSnap.empty) {
      const inviteDoc = inviteSnap.docs[0]
      const inviteData = inviteDoc.data()
      organizationIdToUse = inviteData.organizationId || null
      try {
        await updateDoc(inviteDoc.ref, {
          status: 'accepted',
          acceptedAt: serverTimestamp(),
          acceptedUserId: uid
        })
      } catch (err) {
        console.error('Invite update error:', err)
      }
    }

    if (!organizationIdToUse) {
      const orgRef = await addDoc(collection(db, 'organizations'), {
        ownerId: uid,
        name: email?.split('@')[0] || 'Organization',
        createdAt: serverTimestamp(),
        planName: DEFAULT_PLAN_NAME,
        responseLimit: DEFAULT_RESPONSE_LIMIT,
        responsesUsed: 0,
        responseResetAt: Timestamp.fromDate(getNextResetDate())
      })
      organizationIdToUse = orgRef.id
    }

    // Firestore’daki "users" koleksiyonuna kullanıcı bilgilerini kaydet
    await setDoc(doc(db, 'users', uid), {
      uid,
      email,
      avatarUrl: '',
      nickname: '',
      firstName: '',
      lastName: '',
      organizationName: '',
      billingInfo: '',
      role: 'user',
      status: 'active',
      createdAt: serverTimestamp(),
      emailVerified: false, // <-- email doğrulanmadı olarak başlatıyoruz
      organizationId: organizationIdToUse
    })

    // Email doğrulama maili gönder
    await sendEmailVerification(userCredential.user)

    // Kayıttan sonra kullanıcıyı otomatik çıkış yaptır (isteğe bağlı)
    await signOut(auth)

    return userCredential
  }

  // 2) Email/Password ile Giriş (Login)
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
  }

  // 3) Şifre sıfırlama
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email)
  }

  // 4) Çıkış yapma
  function logout() {
    return signOut(auth)
  }

  // 5) Mevcut kullanıcıyı izleme (onAuthStateChanged)
  useEffect(() => {
    let unsubscribeUserDoc = null

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (user.isAnonymous) {
          if (unsubscribeUserDoc) {
            unsubscribeUserDoc()
            unsubscribeUserDoc = null
          }
          setCurrentUser(null)
          setUserRole(null)
          setUserStatus(null)
          setOrganization(null)
          setIsAnonymousUser(true)
          setAuthed(false)
          setLoading(false)
          return
        }

        if (unsubscribeUserDoc) {
          unsubscribeUserDoc()
        }
        setIsAnonymousUser(false)

        const userDocRef = doc(db, 'users', user.uid)
        let initialUserData = {}

        try {
          const userDocSnap = await getDoc(userDocRef)
          if (userDocSnap.exists()) {
            initialUserData = userDocSnap.data()
          } else {
            const fallbackData = {
              uid: user.uid,
              email: user.email,
              role: 'user',
              status: 'active',
              createdAt: serverTimestamp(),
              emailVerified: !!user.emailVerified
            }
            await setDoc(userDocRef, fallbackData)
            initialUserData = fallbackData
          }

          if (user.emailVerified && initialUserData && !initialUserData?.emailVerified) {
            await updateDoc(userDocRef, { emailVerified: true })
          }
        } catch (error) {
          console.error('User document fetch error:', error)
        }

        const resolvedOrg = await ensureOrganizationForUser(user, initialUserData, userDocRef)
        if (resolvedOrg) {
          setOrganization(resolvedOrg)
        } else {
          setOrganization(null)
        }

        unsubscribeUserDoc = onSnapshot(userDocRef, (snapshot) => {
          if (snapshot.exists()) {
            const userData = snapshot.data()
            const orgId = userData.organizationId || resolvedOrg?.id || null

            setCurrentUser({
              uid: user.uid,
              email: user.email,
              avatarUrl: userData.avatarUrl || '',
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              organizationName: userData.organizationName || '',
              billingInfo: userData.billingInfo || '',
              organizationId: orgId
            })
            setUserRole(userData.role || 'user')
            setUserStatus(userData.status || 'active')

            if (orgId) {
              loadOrganization(orgId)
            } else {
              setOrganization(null)
            }
          } else {
            setCurrentUser({
              uid: user.uid,
              email: user.email,
              avatarUrl: user.avatarUrl || '',
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              organizationName: user.organizationName || '',
              billingInfo: user.billingInfo || '',
              organizationId: resolvedOrg?.id || null
            })
            setUserRole('user')
            setUserStatus('active')
            setOrganization(resolvedOrg || null)
          }
          setAuthed(true)
          setLoading(false)
        })
      } else {
        if (unsubscribeUserDoc) {
          unsubscribeUserDoc()
          unsubscribeUserDoc = null
        }
        // Kullanıcı yoksa state sıfırla
        setCurrentUser(null)
        setUserRole(null)
        setUserStatus(null)
        setOrganization(null)
        setIsAnonymousUser(false)
        setAuthed(false)
        setLoading(false)
      }
    })

    // Cleanup
    return () => {
      unsubscribeAuth()
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc()
      }
    }
  }, [])

  // Context olarak döndüreceğimiz değerler
  const value = {
    currentUser,
    userRole,
    organization,
    loading,
    authed,
    userStatus,
    setAuthed,
    isAnonymousUser,
    register,
    login,
    resetPassword,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {/* loading false olana kadar children'ı göstermeyelim */}
      {!loading && children}
    </AuthContext.Provider>
  )
}
